import i18nService from "@/services/i18n"


export class Entry {
    orderIndex: number

    /** ID */
    id: number | null

    /** КБК респ. уровня */
    republicKbk: string

    /** КБК мест. уровня */
    localKbk: string

    /** Регион */
    regionCode: string
    regionNameRu: string
    regionNameKk: string

    projectCode: string
    projectNameRu: string
    projectNameKk: string

    /** Разрешено доиспользовать по решению Правительства Республики Казахстан за счёт остатков местного бюджета на ____ год */
    allowedSum: number | null

    /** Принятые обязательства */
    acceptedObligations: number | null

    /** Оплаченные обязательства */
    paidObligaitons: number | null

    /** Возвращено в доход вышестоящего бюджета */
    private _returnedSum: number | null = null

    /** Причины неосвоения (с указанием номера платёжного поручения и даты перечисления неосвоенных средств) */
    reasons: Reason[]

    constructor(
        orderIndex: number,
        id: number | null,
        republicKbk: string,
        localKbk: string,
        regionCode: string,
        regionNameRu: string,
        regionNameKk: string,
        projectCode: string,
        projectNameRu: string,
        projectNameKk: string,
        allowedSum: number | null,
        acceptedObligations: number | null,
        paidObligaitons: number | null,
        returnedSum: number | null,
        reasons: Reason[],
    ) {
        this.orderIndex = orderIndex
        this.id = id
        this.republicKbk = republicKbk
        this.localKbk = localKbk
        this.regionCode = regionCode
        this.regionNameRu = regionNameRu
        this.regionNameKk = regionNameKk
        this.projectCode = projectCode
        this.projectNameRu = projectNameRu
        this.projectNameKk = projectNameKk
        this.allowedSum = allowedSum
        this.acceptedObligations = acceptedObligations
        this.paidObligaitons = paidObligaitons
        this._returnedSum = returnedSum
        this.reasons = reasons
    }

    /** Возвращено в доход вышестоящего бюджета */
    get returnedSum(): number | null {
        if (this.undevelopedSum === 0) return 0
        return this._returnedSum
    }

    set returnedSum(value: number | null) {
        this._returnedSum = value
    }

    /** Неосвоение за отчётный период */
    get undevelopedSum(): number {
        return (this.paidObligaitons ?? 0) - (this.allowedSum ?? 0)
    }

    /** КБК мест. уровня (форматированный 123.456.789) */
    get formattedLocalKbk(): string {
        return this.localKbk.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    }

    /** КБК респ. уровня (форматированный 123.456.789) */
    get formattedRepublicKbk(): string {
        return this.republicKbk.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    }

    /** Инвестиционный проект */
    get project(): string {
        if (!this.projectName) {
            return this.projectCode
        }
        return `${this.projectCode} - ${this.projectName}`
    }

    get projectName(): string {
        switch (i18nService.locale) {
            case 'kk':
                return this.projectNameKk
            case 'en':
                return this.projectNameRu
            case 'ru':
                return this.projectNameRu
            default:
                return this.projectNameRu
        }
    }

    /** Регион */
    get region(): string {
        let name = this.regionNameRu
        switch (i18nService.locale) {
            case 'kk':
                name = this.regionNameKk
                break
            case 'en':
                name = this.regionNameRu
                break
            case 'ru':
                name = this.regionNameRu
                break
            default:
                name = this.regionNameRu
        }

        if (!name) {
            return this.regionCode
        }

        return `${this.regionCode} - ${name}`
    }

    get hasFlcErrors(): boolean {
        // Check 1: allowedSum >= acceptedObligations >= paidObligaitons
        if (
            !((this.allowedSum??0) >= (this.acceptedObligations??0)
            && (this.acceptedObligations??0) >= (this.paidObligaitons??0))
        ) {
            return true
        }

        // Check 2: reasonSum == undevelopedSum
        const reasonSum = this.reasons.reduce((acc, r) => acc + r.sum, 0)
        if (reasonSum !== Math.abs(this.undevelopedSum)) {
            return true
        }

        // Check 3: returnedSum <= undevelopedSum
        if (!((this.returnedSum??0) <= Math.abs(this.undevelopedSum))) {
            return true
        }

        return false
    }
}

export class Reason {

    /** ID */
    id: number

    /** Сумма */
    sum: number

    /** Причина на русском */
    reasonRu: string

    /** Причина на казахском */
    reasonKk: string

    constructor(
        id: number,
        sum: number,
        reasonRu: string,
        reasonKk: string,
    ) {
        this.id = id
        this.sum = sum
        this.reasonRu = reasonRu
        this.reasonKk = reasonKk
    }

    get reason(): string {
        if (i18nService.locale === 'kk') {
            return this.reasonKk
        }
        return this.reasonRu
    }

    get sumAndReason(): string {
        return `${formatDecimal(this.sum)} - ${this.reason}`
    }
}

export function parseFormattedNumber(value: string): number {
    if (!value) {
        return 0
    }

    let cleaned = String(value).trim()

    // Strip thousand separators (spaces)
    cleaned = cleaned.replace(/\s/g, "")

    // Keep only the first . or , as the decimal separator, strip any extras
    cleaned = cleaned.replace(/[.,]/g, (m, i) => i === cleaned.search(/[.,]/) ? m : '')

    // Normalize decimal separator to a dot for parsing
    cleaned = cleaned.replace(",", ".")

    return Number(cleaned) || 0
}

export function formatDecimal(value: string | number | null): string | null {
    if (value == null || value === '') {
        return null
    }

    let formatted: string = String(value).trim()

    // Reject input with more than one decimal separator (either . or ,)
    const separatorCount = (formatted.match(/[.,]/g) || []).length
    if (separatorCount > 1) {
        formatted = formatted.replace(/[.,]/g, (m, i) => i === formatted.search(/[.,]/) ? m : '')
        return formatted
    }

    // Split into integer and fractional parts, treating either . or , as the decimal separator
    const decimalMatch = formatted.replace(/\s/g, "").match(/^(-?\d+)(?:[.,](\d+))?$/)
    if (!decimalMatch) {
        return formatted
    }

    const [, integerPart, fractionPart] = decimalMatch

    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ")

    if (!fractionPart) {
        return formattedInteger
    }

    const truncatedFraction = fractionPart.slice(0, 2)

    return `${formattedInteger}.${truncatedFraction}`
}
